/**
 * Alert tone by sim alert TYPE (never by wording). Only types that report a failure, loss, risk or an
 * unmet condition are "problem" (red, warning icon). Everything else, including successes, confirmations,
 * decisions, season notes and any type this list does not know, stays neutral rather than asserting an error.
 * Verified against the pushAlert/setCondition emitters in src/sim/systems (2026-10-22).
 */
const PROBLEM_TYPES = new Set([
  'negative_cash', 'rare_animal_died', 'animal_died', 'animal_escaped', 'animal_ill', 'animal_lost', 'bachelor_injury', 'breeding_failed', 'camping_incident', 'carbon_audit_failed',
  'challenge_expired', 'command_rejected', 'contract_expired', 'crate_lost', 'decision_expired', 'drought', 'drought_start',
  'expedite_scandal', 'grant_breached', 'grant_failed', 'handler_injury', 'hybrid_stillborn', 'import_disease', 'inbred_offspring',
  'inspection_failed', 'insurance_lapsed', 'license_expired', 'license_expiring', 'loan_missed_payment', 'low_cash', 'male_surplus',
  'meat_inspection_failed', 'meat_spoiled', 'newborn_lost', 'overgrazed', 'poaching', 'predator_cull', 'predator_kill',
  'scenario_failed', 'species_fee_unpaid', 'staff_overwork', 'staff_quit', 'storm_damage', 'tax_unpaid', 'tour_upkeep_unpaid',
  'vet_backlog', 'repair_backlog', 'unfed_animals', 'water_shortage', 'welfare_critical', 'wildfire',
]);

export function isProblemAlert(type: string | undefined): boolean {
  return type !== undefined && PROBLEM_TYPES.has(type);
}

export interface AlertItem { text: string; type: string | undefined; refId: string | null }
interface EventLike { day: number; type: string; refId: string | null; message: string }

const textKey = (a: string): string => a.toLowerCase().replace(/\d+/g, '#').replace(/\s+/g, ' ').trim();

/**
 * Feed items in order, oldest first. Typed events with a refId are identified by the sim's own identity
 * (type + refId + day), so two completions that read alike stay separate. Typed events without a refId and
 * untyped strings (older than today, or not carried by alertsDetailed) still collapse by digit-normalized text.
 */
export function buildAlertFeed(alerts: string[], events: EventLike[]): AlertItem[] {
  const pool = events.map((e) => e.message);
  const legacy: string[] = [];
  for (const a of alerts) {
    const i = pool.indexOf(a);
    if (i >= 0) pool.splice(i, 1); else legacy.push(a);
  }
  const seen = new Set<string>();
  const out: AlertItem[] = [];
  const add = (item: AlertItem, key: string): void => {
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  };
  for (const a of legacy) add({ text: a, type: undefined, refId: null }, `t:${textKey(a)}`);
  for (const e of events) {
    // command_rejected collapses by reason text, not refId: each refused tap carries a unique
    // commandId, so refId-keying let repeated identical taps flood the feed. Same refusal stays
    // one item; a genuinely different refusal reason is a different key and always shows.
    const key = e.refId === null || e.type === 'command_rejected' ? `t:${textKey(e.message)}` : `id:${e.type}:${e.refId}:${String(e.day)}`;
    add({ text: e.message, type: e.type, refId: e.refId }, key);
  }
  return out;
}

/** Species id and completion tick from an observe_complete alert's identity (refId observe:<species>:<tick>); never from its wording. */
export function observeAlertIdentity(a: AlertItem): { species: string; tick: number } | null {
  if (a.type !== 'observe_complete' || a.refId === null) return null;
  const parts = a.refId.split(':');
  const species = parts.at(1) ?? '';
  const tick = parts.at(2) ?? '';
  const n = tick === '' ? NaN : Number(tick);
  return parts.at(0) === 'observe' && species !== '' && Number.isFinite(n) ? { species, tick: n } : null;
}

/** Severity is type-based; no parsing of natural-language messages. */
export function alertSeverity(type: string | undefined): 'info' | 'warning' | 'critical' {
  if (type === 'animal_escaped' || type === 'rare_animal_died' || type === 'negative_cash' || type === 'animal_ill') return 'critical';
  return isProblemAlert(type) ? 'warning' : 'info';
}
